import { createSelector } from 'reselect';
import {
  DEMO_UPDATE_SCORE,
  DEMO_UPDATE_NAME,
  DEMO_UPDATE_TAG,
  DEMO_SAVE,
  DEMO_SAVE_SUCCESS,
  DEMO_SAVE_FAIL,
  DEMO_RESET } from './constants';
import {
  ADMIN_SCORE_FETCH_ALL_SUCCESS
} from '../admin/scoreDefinitions/constants';
import { getScoreDefinitions } from '../admin/scoreDefinitions/reducer';
import { getCategory } from '../admin/scoreDefinitions/util';

let initialState = {
  scores: { // set the score order
    fertility: null,
    calvingEase: null,
    growthEfficiency: null,
    temperament: null,
    structure: null,
    size: null,
    welfare: null,
    health: null,
    medication: null,
    breed: null,
    homeBred: null,
    pastureFed: null,
    flavour: null,
    texture: null,
    fatCover: null,
    pgiStatus: null,
    nutritionalValue: null,
    retailValue: null,
    carbonSequestation: null,
    methaneMitigation: null,
    croppingGhgReduction: null,
    soilCondition: null,
    traceElements: null,
    plantVarieties: null,
    pastureVsGrain: null,
    antibiotics: null,
    waterQuality: null
  },
  name: null,
  tag: null,
  id: null,
  saving: false,
  apiValidationError: null
};
const emptyState = { ...initialState };

// initialState = {
//   scores: {
//     fertility: 4,
//     calvingEase: 4,
//     growthEfficiency: 4,
//     temperament: 4,
//     structure: 4,
//     size: 4,
//     welfare: 4,
//     health: 8,
//     medication: 3,
//     breed: 4,
//     homeBred: 4,
//     pastureFed: 4,
//     flavour: 3,
//     texture: 3,
//     fatCover: 3,
//     pgiStatus: 4,
//     nutritionalValue: 8,
//     retailValue: 2,
//     carbonSequestation: 8,
//     methaneMitigation: 3,
//     croppingGhgReduction: 2,
//     soilCondition: 3,
//     traceElements: 3,
//     plantVarieties: 2,
//     pastureVsGrain: 4,
//     antibiotics: 3,
//     waterQuality: 3
//   },
//   name: 'New Animal',
//   tag: 'UK 123456 123456',
//   id: null,
//   saving: false,
//   apiValidationError: null
// };

const demo = (state = initialState, action) => {
  switch (action.type) {
    case ADMIN_SCORE_FETCH_ALL_SUCCESS: {
      const scores = JSON.parse(JSON.stringify(state.scores));
      action.scores.forEach(score => {
        if (typeof scores[score.key] === 'undefined') {
          scores[score.key] = null;
        }
      });
      return { scores };
    }
    case DEMO_UPDATE_SCORE: {
      const scores = { ...state.scores };
      scores[action.attribute] = action.value;
      return { ...state, scores };
    }
    case DEMO_UPDATE_NAME:
      return { ...state, name: action.name };
    case DEMO_UPDATE_TAG:
      return { ...state, tag: action.tag };
    case DEMO_SAVE:
      return { ...state, saving: true };
    case DEMO_SAVE_SUCCESS:
      return { ...state,
        saving: false,
        id: action.animal.id,
        apiValidationError: null
      };
    case DEMO_SAVE_FAIL:
      return { ...state, saving: false, apiValidationError: action.error };
    case DEMO_RESET: {
      const newState = { ...emptyState };
      Object.keys(state.scores).forEach(factor => {
        newState.scores[factor] = null;
      });
      return { ...state, ...newState };
    }
  }
  return state;
};

const scores = state => state.demo.scores;
const name = state => state.demo.name;
const tag = state => state.demo.tag;
const id = state => state.demo.id;
const saving = state => state.demo.saving;
const apiValidationError = state => state.demo.apiValidationError;

export const demoIsSaving = createSelector(
  saving, saving => saving
);

export const getDemoId = createSelector(
  id, id => id
);

export const getApiError = createSelector(
  apiValidationError, error => error
);

export const getScores = createSelector(
  scores, scores => scores
);

export const getName = createSelector(name, name => name);
export const getTag = createSelector(tag, tag => tag);

export const proportionComplete = createSelector(
  scores, scores => {
    const factors = Object.values(scores);
    let completionCount = 0;
    factors.forEach(factor => {
      if (typeof factor === 'number') completionCount++;
    });
    return completionCount / factors.length;
  }
);

export const tagComplete = createSelector(
  tag, tag => !!tag
);

export const getNextIncompleteScoreAttribute = createSelector(
  scores, scores => {
    const attributes = Object.keys(scores);
    for (let i = 0; i < attributes.length; i++) {
      if (scores[attributes[i]]) return attributes[i];
    }
    return null;
  }
);

export const getFinalScore = createSelector(
  scores, scores => Object.values(scores).reduce((a, b) => a + b, 0)
);

export const getProductionData = createSelector(
  [scores, getScoreDefinitions], (scores, scoreDefintions) => {
    const newScores = { ...scores };
    Object.keys(scores).forEach(key => {
      if (getCategory(key, scoreDefintions) !== 'production') {
        delete newScores[key];
      }
    });
    return newScores;
  }
);

export const getEatingData = createSelector(
  [scores, getScoreDefinitions], (scores, scoreDefintions) => {
    const newScores = { ...scores };
    Object.keys(scores).forEach(key => {
      if (getCategory(key, scoreDefintions) !== 'eating') {
        delete newScores[key];
      }
    });
    return newScores;
  }
);

export const getEnvironmentData = createSelector(
  [scores, getScoreDefinitions], (scores, scoreDefintions) => {
    const newScores = { ...scores };
    Object.keys(scores).forEach(key => {
      if (getCategory(key, scoreDefintions) !== 'environment') {
        delete newScores[key];
      }
    });
    return newScores;
  }
);

export const getAnimalForSaving = createSelector(
  [ getProductionData, getEatingData, getEnvironmentData, name, tag ],
  (productionFromDemo, eatingFromDemo, environmentFromDemo, name, tag) => {
    const production = {};
    const eating = {};
    const environment = {};
    Object.keys(productionFromDemo).forEach(key => {
      production[key] = {
        score: productionFromDemo[key], supportingInfo: []
      };
    });
    Object.keys(eatingFromDemo).forEach(key => {
      eating[key] = {
        score: eatingFromDemo[key], supportingInfo: []
      };
    });
    Object.keys(environmentFromDemo).forEach(key => {
      environment[key] = {
        score: environmentFromDemo[key], supportingInfo: []
      };
    });
    return { name, tag, production, eating, environment };
  }
);
export default demo;
